import { Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  CheckoutExtensionUI,
  CheckoutUpsellExtension,
  ICheckoutExtensions,
  UpsellFreeShippingThresholdReplaceValues,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { IUpsellThresholdTypes } from '@pdqdev/pdq-shared/dist/interfaces/IThresholdTypes';
import UpsellTextType = CheckoutUpsellExtension.UpsellTextType;
import GoalsDisplayMode = CheckoutUpsellExtension.GoalsDisplayMode;

import Goal = CheckoutUpsellExtension.Goal;
import GoalThreshold = CheckoutUpsellExtension.GoalThreshold;

export { Goal, GoalThreshold };

interface DropdownOption {
  label: string;
  value: string;
}

@Component({
  selector: 'checkout-upsell-goals',
  templateUrl: './checkout-upsell-goals.component.html',
  styleUrls: ['./checkout-upsell-goals.component.scss'],
  standalone: false,
})
export class CheckoutUpsellGoalsComponent {
  @Input() formGroup: UntypedFormGroup;
  @Input() defaultCountry: string;
  @Input() defaultCurrency: string;

  goalThresholdsMap: Map<string, UntypedFormArray> = new Map();
  thresholdErrors: Map<string, string> = new Map();

  constructor(private elementRef: ElementRef) {}

  @Input() goal1Enabled: boolean = true;
  @Input() freeThresholdsFormArray: UntypedFormArray | null = null;
  @Input() thresholdType: string = IUpsellThresholdTypes.CartValue;
  @Input() currencyCodes: string[] = [];
  @Input() offerType: string = '';
  @Input() isPreviewThresholdMode: boolean = true;
  @Input() textWithoutThreshold: string = '';
  @Input() textWithoutThresholdError: string = '';
  @Input() showMessageIfFreeShippingAvailable: boolean = false;
  @Input() textWithThreshold: string = '';
  @Input() textWithThresholdError: string = '';
  @Input() showInfoInProgressBar: boolean = false;
  @Input() progressBarLabel: string = '';
  @Input() giftUnlockExtensionsByOfferType: Record<string, { label: string; value: number }[]> = {};
  @Input() selectedGiftUnlockExtension: number | null = null;
  @Input() selectedGiftUnlockExtensionObject: ICheckoutExtensions | null = null;
  @Input() goals: Goal[] = [];
  @Input() giftUnlockExtensionsMap: Map<number, ICheckoutExtensions> = new Map();

  private previewedGoalIndex: number = 0;
  private previewedTextType: UpsellTextType = UpsellTextType.Threshold;

  @Output() goal1EnabledChange = new EventEmitter<boolean>();
  @Output() addThreshold = new EventEmitter<void>();
  @Output() removeThreshold = new EventEmitter<number>();
  @Output() offerTypeChange = new EventEmitter<string>();
  @Output() isPreviewThresholdModeChange = new EventEmitter<boolean>();
  @Output() previewTextChange = new EventEmitter<{ goalIndex: number; textType: UpsellTextType }>();
  @Output() textWithoutThresholdChange = new EventEmitter<string>();
  @Output() showMessageIfFreeShippingAvailableChange = new EventEmitter<boolean>();
  @Output() textWithThresholdChange = new EventEmitter<string>();
  @Output() showInfoInProgressBarChange = new EventEmitter<boolean>();
  @Output() progressBarLabelChange = new EventEmitter<string>();
  @Output() giftUnlockExtensionChange = new EventEmitter<number>();
  @Output() goalsChange = new EventEmitter<Goal[]>();
  @Output() addGoalEvent = new EventEmitter<void>();
  @Output() removeGoalEvent = new EventEmitter<number>();

  get goalsConfig(): UntypedFormGroup {
    return this.formGroup.get('goalsConfiguration') as UntypedFormGroup;
  }

  readonly IUpsellThresholdTypes = IUpsellThresholdTypes;
  readonly UpsellFreeShippingThresholdReplaceValues = UpsellFreeShippingThresholdReplaceValues;
  readonly UpsellTextType = UpsellTextType;
  readonly GoalsDisplayMode = GoalsDisplayMode;

  private readonly DEFAULT_ICON_URLS = {
    'free-shipping': 'https://pdq-shopify-content.s3.us-east-2.amazonaws.com/extensions-default/upsell/truck.png',
    product: 'https://pdq-shopify-content.s3.us-east-2.amazonaws.com/extensions-default/upsell/gift.png',
    coupon: 'https://pdq-shopify-content.s3.us-east-2.amazonaws.com/extensions-default/upsell/percentage.png',
  };

  readonly borderOptions: DropdownOption[] = [
    { label: 'None', value: CheckoutExtensionUI.BorderType.NONE },
    { label: 'Base', value: CheckoutExtensionUI.BorderType.BASE },
    { label: 'Dotted', value: CheckoutExtensionUI.BorderType.DOTTED },
  ];

  readonly borderWidthOptions: DropdownOption[] = [
    { label: 'Base', value: CheckoutExtensionUI.BorderWidth.BASE },
    { label: 'Medium', value: CheckoutExtensionUI.BorderWidth.MEDIUM },
  ];

  readonly cornerRadiusOptions: DropdownOption[] = [
    { label: 'None', value: CheckoutExtensionUI.CornerRadius.NONE },
    { label: 'Small', value: CheckoutExtensionUI.CornerRadius.SMALL },
    { label: 'Base', value: CheckoutExtensionUI.CornerRadius.BASE },
    { label: 'Large', value: CheckoutExtensionUI.CornerRadius.LARGE },
    { label: 'Fully Rounded', value: CheckoutExtensionUI.CornerRadius.FULLY_ROUNDED },
  ];

  readonly paddingOptions: DropdownOption[] = [
    { label: 'None', value: CheckoutExtensionUI.Padding.NONE },
    { label: 'Small 100', value: CheckoutExtensionUI.Padding.SMALL100 },
    { label: 'Small 200', value: CheckoutExtensionUI.Padding.SMALL200 },
    { label: 'Small 300', value: CheckoutExtensionUI.Padding.SMALL300 },
    { label: 'Small 400', value: CheckoutExtensionUI.Padding.SMALL400 },
    { label: 'Small 500', value: CheckoutExtensionUI.Padding.SMALL500 },
    { label: 'Base', value: CheckoutExtensionUI.Padding.BASE },
    { label: 'Large 100', value: CheckoutExtensionUI.Padding.LARGE100 },
    { label: 'Large 200', value: CheckoutExtensionUI.Padding.LARGE200 },
    { label: 'Large 300', value: CheckoutExtensionUI.Padding.LARGE300 },
    { label: 'Large 400', value: CheckoutExtensionUI.Padding.LARGE400 },
    { label: 'Large 500', value: CheckoutExtensionUI.Padding.LARGE500 },
  ];

  readonly labelSizeOptions: DropdownOption[] = [
    { label: 'Extra Small', value: CheckoutExtensionUI.TextSize.EXTRA_SMALL },
    { label: 'Small', value: CheckoutExtensionUI.TextSize.SMALL },
    { label: 'Base', value: CheckoutExtensionUI.TextSize.BASE },
    { label: 'Medium', value: CheckoutExtensionUI.TextSize.MEDIUM },
    { label: 'Large', value: CheckoutExtensionUI.TextSize.LARGE },
    { label: 'Extra Large', value: CheckoutExtensionUI.TextSize.EXTRA_LARGE },
  ];

  readonly offerTypeOptions: DropdownOption[] = [
    { label: 'Free shipping', value: 'free-shipping' },
    { label: 'Coupon', value: 'coupon' },
    { label: 'Product', value: 'product' },
  ];

  onGoal1EnabledChange(value: boolean): void {
    this.goal1Enabled = value;
    this.goal1EnabledChange.emit(value);
  }

  addFreeShippingThreshold(): void {
    this.addThreshold.emit();
  }

  removeFreeShippingThreshold(index: number): void {
    this.removeThreshold.emit(index);
  }

  onOfferTypeChange(value: string): void {
    this.offerType = value;
    this.offerTypeChange.emit(value);
  }

  togglePreviewMode(): void {
    this.isPreviewThresholdMode = !this.isPreviewThresholdMode;
    this.isPreviewThresholdModeChange.emit(this.isPreviewThresholdMode);
  }

  togglePreviewText(goalIndex: number, textType: UpsellTextType): void {
    this.previewedGoalIndex = goalIndex;
    this.previewedTextType = textType;

    this.previewTextChange.emit({ goalIndex, textType });

    this.isPreviewThresholdMode = textType === UpsellTextType.Threshold;
    this.isPreviewThresholdModeChange.emit(this.isPreviewThresholdMode);
  }

  isTextVisible(goalIndex: number, textType: UpsellTextType): boolean {
    return this.previewedGoalIndex === goalIndex && this.previewedTextType === textType;
  }

  onShowInfoInProgressBarChange(value: boolean): void {
    this.showInfoInProgressBar = value;
    this.showInfoInProgressBarChange.emit(value);
  }

  onProgressBarLabelChange(value: string): void {
    this.progressBarLabel = value;
    this.progressBarLabelChange.emit(value);
  }

  onIconPreviewChange(goalIndex: number, value: string): void {
    this.updateGoal(goalIndex, { iconUrl: value });
  }

  onClearIcon(goalIndex: number): void {
    this.updateGoal(goalIndex, { iconUrl: '' });
  }

  onGiftUnlockExtensionChange(value: number): void {
    this.selectedGiftUnlockExtension = value;
    this.giftUnlockExtensionChange.emit(value);
  }

  addGoal(): void {
    this.addGoalEvent.emit();
  }

  removeGoal(goalIndex: number): void {
    this.removeGoalEvent.emit(goalIndex);
  }

  onGoalEnabledChange(goalIndex: number, value: boolean): void {
    this.updateGoal(goalIndex, { enabled: value });
  }

  onGoalOfferTypeChange(goalIndex: number, value: string): void {
    const defaultIcon = this.DEFAULT_ICON_URLS[value] || this.DEFAULT_ICON_URLS['free-shipping'];
    this.updateGoal(goalIndex, { offerType: value, iconUrl: defaultIcon });
    this.offerTypeChange.emit(value);
  }

  onGoalGiftUnlockExtensionChange(goalIndex: number, value: number): void {
    this.updateGoal(goalIndex, { selectedGiftUnlockExtension: value });
  }

  onGoalShowInfoInProgressBarChange(goalIndex: number, value: boolean): void {
    this.updateGoal(goalIndex, { showInfoInProgressBar: value });
  }

  onGoalProgressBarLabelChange(goalIndex: number, value: string): void {
    this.updateGoal(goalIndex, { progressBarLabel: value });
  }

  private updateGoal(goalIndex: number, changes: Partial<Goal>): void {
    if (!this.goals[goalIndex]) {
      return;
    }
    const updatedGoals = this.goals.map((goal, i) => (i === goalIndex ? { ...goal, ...changes } : goal));
    this.goalsChange.emit(updatedGoals);
  }

  getGiftUnlockExtensionsForGoal(goalIndex: number): { label: string; value: number }[] {
    const offerType = this.goals[goalIndex]?.offerType;
    const all = this.giftUnlockExtensionsByOfferType[offerType] || [];
    const claimedByOtherGoals = new Set(
      this.goals
        .map((g, i) => (i !== goalIndex ? g?.selectedGiftUnlockExtension : null))
        .filter((id): id is number => typeof id === 'number'),
    );
    return all.filter((opt) => !claimedByOtherGoals.has(opt.value));
  }

  getSelectedGiftUnlockOption(goalIndex: number): { label: string; value: number } | null {
    const selectedId = this.goals[goalIndex]?.selectedGiftUnlockExtension;
    if (typeof selectedId !== 'number') return null;
    const offerType = this.goals[goalIndex]?.offerType;
    const all = this.giftUnlockExtensionsByOfferType[offerType] || [];
    return all.find((opt) => opt.value === selectedId) ?? null;
  }

  getSelectedGiftUnlockExtensionObject(goalIndex: number): ICheckoutExtensions | null {
    const selectedId = this.goals[goalIndex]?.selectedGiftUnlockExtension;
    return selectedId ? this.giftUnlockExtensionsMap.get(selectedId) || null : null;
  }

  getTextWithThreshold(goalIndex: number): string {
    if (goalIndex === 0) {
      return this.formGroup?.get('settings.textWithThreshold')?.value || '';
    }
    return this.goals[goalIndex]?.textWithThreshold || '';
  }

  getTextWithoutThreshold(goalIndex: number): string {
    if (goalIndex === 0) {
      return this.formGroup?.get('settings.textWithoutThreshold')?.value || '';
    }
    return this.goals[goalIndex]?.textWithoutThreshold || '';
  }

  getShowMessageIfFreeShippingAvailable(goalIndex: number): boolean {
    if (goalIndex === 0) {
      return this.formGroup?.get('settings.showMessageIfFreeShippingAvailable')?.value ?? false;
    }
    return this.goals[goalIndex]?.showMessageIfFreeShippingAvailable ?? false;
  }

  onTextWithThresholdChange(goalIndex: number, value: string): void {
    if (goalIndex === 0) {
      this.formGroup?.get('settings.textWithThreshold')?.setValue(value);
    } else {
      this.updateGoal(goalIndex, { textWithThreshold: value });
    }
  }

  onTextWithoutThresholdChange(goalIndex: number, value: string): void {
    if (goalIndex === 0) {
      this.formGroup?.get('settings.textWithoutThreshold')?.setValue(value);
    } else {
      this.updateGoal(goalIndex, { textWithoutThreshold: value });
    }
  }

  onShowMessageChange(goalIndex: number, value: boolean): void {
    if (goalIndex === 0) {
      this.formGroup?.get('settings.showMessageIfFreeShippingAvailable')?.setValue(value);
    } else {
      this.updateGoal(goalIndex, { showMessageIfFreeShippingAvailable: value });
    }
  }

  getThresholdsFormArray(goalIndex: number): UntypedFormArray {
    if (goalIndex === 0) {
      return this.freeThresholdsFormArray;
    }

    const goal = this.goals[goalIndex];
    if (!goal?.id) {
      return new UntypedFormArray([]);
    }

    if (!this.goalThresholdsMap.has(goal.id)) {
      const thresholdsArray = new UntypedFormArray(
        (goal.thresholds || []).map((thresholdConfig: GoalThreshold) => {
          const countriesAndRegionsValue = thresholdConfig?.countriesAndRegions || { [this.defaultCountry]: [] };
          return new UntypedFormGroup({
            countriesAndRegions: new UntypedFormControl(countriesAndRegionsValue),
            threshold: new UntypedFormControl(thresholdConfig?.threshold ?? 0),
            currency: new UntypedFormControl(thresholdConfig?.currency ?? this.defaultCurrency),
          });
        }),
      );
      this.goalThresholdsMap.set(goal.id, thresholdsArray);

      const goalId = goal.id;
      thresholdsArray.valueChanges.subscribe(() => {
        const currentIndex = this.goals.findIndex((g) => g.id === goalId);
        if (currentIndex > 0) {
          this.syncGoalThresholds(currentIndex);
        }
      });
    }

    return this.goalThresholdsMap.get(goal.id)!;
  }

  addThresholdForGoal(goalIndex: number): void {
    if (goalIndex === 0) {
      this.addThreshold.emit();
    } else {
      const thresholdsArray = this.getThresholdsFormArray(goalIndex);

      thresholdsArray.push(
        new UntypedFormGroup({
          countriesAndRegions: new UntypedFormControl({ [this.defaultCountry]: [] }),
          threshold: new UntypedFormControl(0),
          currency: new UntypedFormControl(this.defaultCurrency),
        }),
      );

      this.syncGoalThresholds(goalIndex);
    }
  }

  removeThresholdForGoal(goalIndex: number, thresholdIndex: number): void {
    if (goalIndex === 0) {
      this.removeThreshold.emit(thresholdIndex);
    } else {
      const thresholdsArray = this.getThresholdsFormArray(goalIndex);
      thresholdsArray.removeAt(thresholdIndex);
      this.syncGoalThresholds(goalIndex);
    }
  }

  getThresholdMinValue(): number {
    return 0;
  }

  getDefaultIconForGoal(goalIndex: number): string {
    const goal = this.goals[goalIndex];
    const offerType = goal?.offerType || 'free-shipping';
    return this.DEFAULT_ICON_URLS[offerType] || this.DEFAULT_ICON_URLS['free-shipping'];
  }

  private syncGoalThresholds(goalIndex: number): void {
    const thresholdsArray = this.getThresholdsFormArray(goalIndex);
    const thresholds = thresholdsArray.value;
    this.updateGoal(goalIndex, { thresholds });
  }

  public syncAllGoalThresholds(): void {
    const updatedGoals = [...this.goals];
    let hasChanges = false;

    this.goals.forEach((goal, index) => {
      if (index === 0) {
        return;
      }
      if (goal.id && this.goalThresholdsMap.has(goal.id)) {
        const thresholdsArray = this.getThresholdsFormArray(index);
        const thresholds = thresholdsArray.value;
        updatedGoals[index] = { ...updatedGoals[index], thresholds };
        hasChanges = true;
      }
    });

    if (hasChanges) {
      this.goalsChange.emit(updatedGoals);
    }
  }

  getThresholdError(goalIndex: number, thresholdIndex: number): string | null {
    return this.thresholdErrors.get(`${goalIndex}-${thresholdIndex}`) || null;
  }

  public validateThresholdOrder(): boolean {
    this.thresholdErrors.clear();

    const allEntries: {
      goalIndex: number;
      thresholdIndex: number;
      threshold: number;
      currency: string;
      countries: string[];
    }[] = [];

    this.goals.forEach((goal, goalIndex) => {
      if (goal.enabled === false) return;

      const thresholdsArray = this.getThresholdsFormArray(goalIndex);
      const thresholds = thresholdsArray?.value || [];

      thresholds.forEach((t: GoalThreshold, tIdx: number) => {
        const countriesAndRegions = t.countriesAndRegions || {};
        const countries = Object.keys(countriesAndRegions);
        const currency = t.currency || '';

        allEntries.push({
          goalIndex,
          thresholdIndex: tIdx,
          threshold: t.threshold ?? 0,
          currency,
          countries,
        });
      });
    });

    let hasErrors = false;

    for (const entry of allEntries) {
      if (entry.goalIndex === 0) continue;

      for (const prevEntry of allEntries) {
        if (prevEntry.goalIndex >= entry.goalIndex) continue;

        if (prevEntry.currency !== entry.currency) continue;

        const sharedCountry = entry.countries.some((c) => prevEntry.countries.includes(c));
        if (!sharedCountry) continue;

        if (entry.threshold <= prevEntry.threshold) {
          const errorMsg = `Goal ${entry.goalIndex + 1} threshold (${entry.threshold}) must be higher than Goal ${prevEntry.goalIndex + 1} threshold (${prevEntry.threshold})`;
          this.thresholdErrors.set(`${entry.goalIndex}-${entry.thresholdIndex}`, errorMsg);
          hasErrors = true;
          break;
        }
      }
    }

    if (hasErrors) {
      // Scroll to first error
      setTimeout(() => {
        const errorEl = this.elementRef.nativeElement.querySelector('.threshold-error');
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }

    return !hasErrors;
  }
}
